import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {Observable, Subject, takeUntil} from 'rxjs';
import {FormArray, FormBuilder, Validators} from '@angular/forms';
import {FormStatus} from '@iw-shop/forms/components/submit-button/submit-button.component';
import {Store} from '@ngrx/store';
import {AppState} from '@store/state/app.state';
import {CustomerService} from '@iw-shop/customer/services/customer.service';
import {UserService} from '@iw-shop/user/services/user.service';
import {User} from '@iw-shop/user/models/user';
import {getSelectedCustomer} from "@store/selectors/user.selector";
import {finalize, take} from "rxjs/operators";
import {faEdit, faMoneyBillAlt, faStar} from "@fortawesome/free-regular-svg-icons";
import {faFileInvoice, faTrash, faUsersCog} from "@fortawesome/free-solid-svg-icons";
import {IconProp} from "@fortawesome/fontawesome-svg-core";
import {ApiUsersSetRequest, ApiUsersUpdateRequest} from "@api/users";
import {UserState} from "@store/state/user.state";
import {updateUserEmail} from "@store/actions/user.action";

interface Grant {
    name: string;
    value: string;
    iconClass: IconProp;
}

@Component({
    selector: 'iw-shop-user-data',
    standalone: false,
    templateUrl: './user-data.component.html',
    styleUrls: ['./user-data.component.scss']
})
export class UserDataComponent implements OnInit, OnDestroy {

    @Output() onUpdateSuccess: EventEmitter<any> = new EventEmitter();

    destroy$: Subject<boolean> = new Subject<boolean>();
    user$: Observable<UserState>;

    loading: boolean = true;

    customerId: string = '';
    users: User[] = [];
    userModal: boolean = false;
    openUser?: User;

    apiError?: {
        notification: string,
        errors?: { key: string, error: string }[]
    };

    form;

    grant: Grant [] = [];

    formStatus: FormStatus = null;

    constructor(protected fb: FormBuilder, protected store: Store<AppState>, protected customerService: CustomerService, protected userService: UserService) {
        this.store.select(getSelectedCustomer)
            .pipe(takeUntil(this.destroy$))
            .subscribe(r => {
                if (r?.id) {
                    this.customerId = r.id;
                    this.loadUsers();
                }
            });
        this.form = this.fb.group({
            userId: this.fb.control(''),
            username: this.fb.control('', [Validators.required, Validators.minLength(2)]),
            email: this.fb.control('', [Validators.required, Validators.email]),
            grant: this.fb.array([]),
            password: this.fb.control(''),
            passwordRetype: this.fb.control('')
        });
        this.user$ = this.store.select(state => state.user)
    }

    ngOnInit(): void {
        this.grant = [
            {name: 'Administrator', value: 'users', iconClass: this.faUsersCog},
            {name: 'Zugriff auf Rechnungen', value: 'invoices', iconClass: this.faFileInvoice},
            {name: 'Bearbeitung der Favoriten', value: 'favorites', iconClass: this.faStar},
            {name: 'Einsicht in Preise', value: 'prices', iconClass: this.faMoneyBillAlt}
        ];
    }

    loadUsers() {
        this.loading = true;
        this.userService.getUsers(this.customerId || '')
            .pipe(
                finalize(() => this.loading = false)
            )
            .subscribe(
                r => {
                    if (r.result?.users?.data) {
                        this.users = r.result.users.data;
                    }
                }
            )
    }

    isUserAdmin(): boolean {
        let isAdmin: boolean = false;
        this.store.select(state => state.storage).pipe(take(1)).subscribe(
            r => {
                if (r?.userId) {
                    this.users.map(u => {
                        if (u.id == r.userId) {
                            let shopAccessForCustomers = u.shopAccessForCustomers.filter(x => x.customerId == this.customerId)[0];
                            if (shopAccessForCustomers &&
                                shopAccessForCustomers.permissions?.grant.includes('users')) {
                                isAdmin = true;
                            }
                        }
                    })
                }
            }
        );
        return isAdmin;
    }

    getActiveUserId() {
        let userId = '';
        this.store.select(state => state.storage).pipe(take(1)).subscribe(
            r => {
                if (r?.userId) {
                    userId = r.userId;
                }
            }
        );
        return userId;
    }

    isThereMoreThenOneAdmin() {
        if (
            this.users.filter(
                u => u.shopAccessForCustomers.filter(
                    a => a.customerId == this.customerId
                )[0].permissions?.grant.includes('users')
            ).length > 1
        ) {
            return true;
        }
        return false;
    }

    isUserId(): boolean {

        if ((this.form.get('userId')?.value?.length ?? 0) > 0) {
            return true;
        }
        return false;
    }

    showUserModal(userId?: string) {
        this.form.reset();
        (this.form.get('grant') as FormArray).clear();
        this.userModal = true;
        this.form.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
        if (userId) {
            this.form.get('password')?.setValidators([]);
            this.userService.getUser(userId, this.customerId || '').subscribe(
                r => {
                    if (r.result?.users.data[0]) {
                        this.openUser = r.result?.users.data[0];
                        this.form.get('userId')?.setValue(r.result?.users.data[0].id);
                        this.form.get('username')?.setValue(r.result?.users.data[0].username);
                        this.form.get('email')?.setValue(r.result?.users.data[0].email);
                        let grant: string[] = [];
                        r.result?.users.data[0].shopAccessForCustomers
                            .filter(access => access.customerId == this.customerId)
                            .map(access => {

                                access.permissions?.grant.map(
                                    g => {
                                        grant.push(g);
                                    }
                                )
                            })
                        this.form.setControl('grant', this.fb.array([]));
                        grant.forEach((val, i) => {
                            this.getGrantAsFormArray().push(this.fb.control(val));
                        });

                    }
                }
            )

        }
        this.form.updateValueAndValidity();
    }

    getGrantAsFormArray() {
        return this.form.get('grant') as FormArray
    }

    isGrant(value: string): boolean {
        let grant: any = this.getGrantAsFormArray().value;
        if (grant && grant.includes(value)) {
            return true;
        }
        return false;

    }

    isGrantByCustomer(shopAccessForCustomers: User['shopAccessForCustomers'], value: string): boolean {
        let isGrant: boolean = false;
        shopAccessForCustomers.map(a => {
            if (a.customerId == this.customerId &&
                a.permissions?.grant.includes(value)) {
                isGrant = true;
            }
        });
        return isGrant;
    }

    changeGrant(value: string) {
        let grant: any[] = [];
        if (this.form.get('grant')?.value) {
            grant = this.form.get('grant')?.value ?? [];
        }
        if (this.isGrant(value)) {
            grant.map(
                (g, i) => {
                    if (g == value) {
                        grant.splice(i, 1);
                    }
                }
            )
        } else {
            grant.push(value);
        }
        // Es handelt sich um einen Admin > Direkt alle Rechte zuweisen, weil er das sowieso selbst tun könnte.
        if (this.isGrant('users')) {
            grant = [];
            this.grant.map(
                g => {
                    grant.push(g.value);
                }
            )
        }
        setTimeout(() => {
            this.form.setControl('grant', this.fb.array([]));
            grant.forEach((val, i) => {
                this.getGrantAsFormArray().push(this.fb.control(val));
            });
        }, 50);


    }

    isFormRequestError(value: string): boolean {
        this.passwordValidation();
        let form = this.form?.get(value);
        return !!(form?.errors &&
            this.formStatus == 'submitForm');
    }

    passwordValidation() {
        this.form.get('passwordRetype')?.setErrors(null);
        if (this.form.get('password')?.value !== this.form.get('passwordRetype')?.value) {
            this.form.get('passwordRetype')?.setErrors({notEquivalent: true});
        }
    }

    setUpdateSuccess() {
        this.onUpdateSuccess.emit();
    }


    submit() {
        this.formStatus = 'submitForm';
        this.passwordValidation();

        if (this.form.valid) {
            this.formStatus = 'loading';
            const getData = (): any => {
                let data: Partial<ApiUsersSetRequest['params']['data']> = {
                    username: this.form.get('username')?.value ?? '',
                    email: this.form.get('email')?.value ?? ''
                };
                if ((this.form.get('password')?.value?.length ?? 0) > 0 &&
                    this.form.get('password')?.value == this.form.get('passwordRetype')?.value) {
                    data.password = this.form.get('password')?.value ?? '';
                }
                return data;
            }

            // Update
            if (this.isUserId()) {
                let user: ApiUsersUpdateRequest['params']['data'] = {
                    ...getData(),
                    id: this.form.get('userId')?.value ?? ''
                }
                if (this.openUser) {
                    user.shopAccessForCustomers = this.openUser?.shopAccessForCustomers
                        .map(access => {
                            if (access.customerId == this.customerId) {
                                access.permissions = {grant: this.getGrantAsFormArray().value};
                            }
                            return access;
                        })
                }

                this.userService.update(user)
                    .subscribe(
                        {
                            next: (r) => {
                                this.formStatus = 'submitForm';
                                if (r.error) {
                                    this.apiError = {notification: r.error.message};
                                    if (r.error.input) {
                                        this.apiError.errors = r.error.input;
                                    }
                                    window.scroll({
                                        top: 0,
                                        left: 0,
                                        behavior: 'smooth'
                                    });
                                } else {
                                    this.formStatus = 'success';
                                    this.loadUsers();
                                    let loggedInUserId = '';
                                    this.store.select(state => state.user).pipe(take(1)).subscribe(u => {
                                        loggedInUserId = u.id ?? ''
                                    });
                                    // Der eingeloggte User wird überarbeitet
                                    if (user.id == loggedInUserId) {
                                        this.store.dispatch(updateUserEmail({email: user.email ?? ''}));
                                    }
                                    setTimeout(() => {
                                        if (r.result?.users.updated) {
                                            this.userModal = false;
                                            this.setUpdateSuccess();
                                        }
                                        this.formStatus = null;
                                    }, 1000);
                                }
                            },
                            complete: () => {
                            },
                            error: () => {
                                this.formStatus = 'submitForm';
                            }
                        }
                    );
                // New
            } else {
                let user: ApiUsersSetRequest['params']['data'] = {
                    ...getData(),
                    role: 'customer',
                    shopAccessForCustomers: [{
                        customerId: this.customerId,
                        permissions: {grant: this.getGrantAsFormArray().value}
                    }]
                };
                this.userService.setUser(user)
                    .subscribe(
                        {
                            next: (r) => {
                                this.formStatus = 'submitForm';
                                if (r.error) {
                                    this.apiError = {notification: r.error.message};
                                    if (r.error.input) {
                                        this.apiError.errors = r.error.input;
                                    }
                                    window.scroll({
                                        top: 0,
                                        left: 0,
                                        behavior: 'smooth'
                                    });
                                } else {
                                    this.formStatus = 'success';
                                    this.loadUsers();
                                    setTimeout(() => {
                                        this.formStatus = null;
                                        this.userModal = false;
                                    }, 1000);
                                }
                            },
                            complete: () => {
                            },
                            error: () => {
                                this.formStatus = 'submitForm';
                            }
                        }
                    );
            }
        }
    }

    deleteUser(username: string) {
        if (confirm("Möchten Sie wirklich den Benutzer " + username + " löschen?")) {
            this.userService.deleteUser(username).subscribe(
                r => {
                    this.loadUsers()
                }
            )
        }
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    protected readonly faEdit = faEdit;
    protected readonly faTrash = faTrash;
    protected readonly faUsersCog = faUsersCog;
    protected readonly faFileInvoice = faFileInvoice;
    protected readonly faMoneyBillAlt = faMoneyBillAlt;
    protected readonly faStar = faStar;
}
